import { createContext, useCallback, useEffect } from 'react';
import type { AxiosInstance } from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import axios_instance_sweep from './sweepApiClient';
import axios_instance_auth from './auth0ApiClient';
import axios_instance_sweep_unauthenticated from './sweepUnauthenticatedApiClient';
import { jwtDecode } from 'jwt-decode';
import { useAccountSelectionContext } from '../../components/account-selection/AccountSelectionContext';

interface SweepApiContextType {
  dashboardAPI: AxiosInstance;
  auth0API: AxiosInstance;
  dashboardUnauthenticatedAPI: AxiosInstance;
}

const ApisContext = createContext<SweepApiContextType>({
  dashboardAPI: axios_instance_sweep,
  auth0API: axios_instance_auth,
  dashboardUnauthenticatedAPI: axios_instance_sweep_unauthenticated,
});

// Auth0 SDK error codes that mean the SDK can no longer obtain a token silently
// (e.g. a stale localStorage cache entry from before `useRefreshTokens` was
// enabled, or a revoked/expired refresh_token). The recovery — per Auth0's own
// SDK docs — is to start a fresh /authorize redirect.
// Source: @auth0/auth0-spa-js JSDoc on `useRefreshTokensFallback` in global.ts
// (also: https://auth0.com/docs/quickstart/spa/react/03-calling-an-api).
const RECOVERABLE_AUTH_ERRORS = ['missing_refresh_token', 'invalid_grant', 'login_required'];

const ApiProvider = ({ children }: any) => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const { setIsSSOUser } = useAccountSelectionContext();

  const setAxiosTokenInterceptor = useCallback(() => {
    axios_instance_sweep.interceptors.request.use(async (config) => {
      let token: string;
      try {
        token = await getAccessTokenSilently();
      } catch (e: any) {
        if (RECOVERABLE_AUTH_ERRORS.includes(e?.error)) {
          // Defense-in-depth for users whose iframe fallback also fails (e.g.
          // SSO cookie expired, third-party cookies blocked). Triggers a real
          // /authorize round-trip; the browser will navigate away before the
          // returned (rejected) promise matters to anyone.
          await loginWithRedirect({
            appState: { returnTo: window.location.pathname + window.location.search },
          });
          throw e;
        }
        throw e;
      }
      const tokenData = jwtDecode(token);
      const isSSOUser =
        !tokenData?.sub?.includes('google-oauth2') && !tokenData?.sub?.includes('auth0');
      setIsSSOUser(isSSOUser);

      const requestConfig = config;
      if (token && requestConfig.headers) {
        (requestConfig.headers as any).Authorization = `Bearer ${token}`;
      }
      return requestConfig;
    });

    axios_instance_sweep.interceptors.response.use(undefined, async (axiosError) => {
      const { statusCode } = axiosError.response?.data || {};
      if (statusCode === 401) {
        const msg = 'User not authorized. Sweep is currently in private beta.';
        console.log(msg);
        return Promise.reject({
          ...axiosError,
          response: {
            ...axiosError.response,
            data: {
              ...axiosError.response.data,
              error: msg,
            },
          },
        });
      }
      return Promise.reject(axiosError);
    });
  }, [getAccessTokenSilently, loginWithRedirect, setIsSSOUser]);

  useEffect(() => {
    setAxiosTokenInterceptor();
  }, [setAxiosTokenInterceptor]);

  return (
    <ApisContext.Provider
      value={{
        dashboardAPI: axios_instance_sweep,
        auth0API: axios_instance_auth,
        dashboardUnauthenticatedAPI: axios_instance_sweep_unauthenticated,
      }}
    >
      {children}
    </ApisContext.Provider>
  );
};

export { ApisContext, ApiProvider };
